/* AR / Collections' "Outstanding" rules, shared by the AR page and the Accounting dashboard's Money to
   collect so the two always show the same figures.

   Collectible = a job invoice with a POSITIVE balance -- matching the accounting team's Open AR
   workbook. ON ACCT rows, CR/DM memos and credit balances are CE bookkeeping (unapplied cash), not
   collectible receivables: the AR mirror keeps them, these totals don't count them. An invoice is
   counted net of the credit memos numbered after it (outstandingOf). */

export const num = (v) => (v == null || v === '' ? 0 : parseFloat(v) || 0);

/* whole days from a → b (both DATE strings); null if either is missing */
export const daysBetween = (a, b) => {
  if (!a || !b) return null;
  const da = new Date(String(a).slice(0, 10) + 'T00:00:00');
  const db = new Date(String(b).slice(0, 10) + 'T00:00:00');
  if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return null;
  return Math.round((db - da) / 86400000);
};

export const isCollectible = (r) => num(r.balance) > 0
  && !String(r.invoice_number || '').toUpperCase().includes('ON ACCT')
  && !/^(CR|DM)/.test(String(r.invoice_number || ''));

/* The invoice an open credit memo is numbered after: 142652CR → 142652, CM142696 → 142696 (CE numbers
   them both ways -- the same rule as Statements' creditOf). Null for anything else. */
const creditTarget = (r) => {
  if (!(num(r.balance) < 0)) return null;
  const n = String(r.invoice_number || '').trim();
  const m = /^CM\s*(.+)$/i.exec(n) || /^(.+?)\s*CR$/i.exec(n);
  return m ? m[1].toUpperCase() : null;
};

/** Outstanding, net of credit memos (Duncan 2026-10-26, from accounting: "if we have a credit Memo that
    has the same invoice number as a active invoice, it needs to move it down to a new section"): the
    collectible rows, each less the open credit memos numbered after it on its job. One they cancel in
    full leaves `open` for `credited` ({ ...row, credits, credit, invoiced, net }); one they cancel in
    part stays in `open` at what's left, carrying `credits`, `credit` (negative) and `invoiced`. */
export function outstandingOf(rows) {
  const memos = new Map(); // `${job}|${invoice}` → the credit memo rows numbered after it
  (rows || []).forEach((r) => {
    const target = creditTarget(r);
    if (!target) return;
    const k = `${r.job_id || ''}|${target}`;
    memos.set(k, [...(memos.get(k) || []), r]);
  });
  const open = [];
  const credited = [];
  (rows || []).filter(isCollectible).forEach((r) => {
    const credits = memos.get(`${r.job_id || ''}|${String(r.invoice_number).trim().toUpperCase()}`);
    if (!credits) { open.push(r); return; }
    const invoiced = num(r.balance);
    const credit = Math.round(credits.reduce((s, c) => s + num(c.balance), 0) * 100) / 100;
    const net = Math.round((invoiced + credit) * 100) / 100;
    if (net > 0.004) open.push({ ...r, balance: net, credits, credit, invoiced });
    else credited.push({ ...r, credits, credit, invoiced, net });
  });
  return { open, credited };
}

/** → { net, overdue, overdueCount, invoices, pctPastDue } over collectible rows. */
export function arOutTotals(rows) {
  const t = { net: 0, overdue: 0, overdueCount: 0, invoices: 0, pctPastDue: 0 };
  (rows || []).forEach((r) => {
    const b = num(r.balance);
    t.net += b;
    t.invoices += 1;
    if ((r.days_past_due || 0) > 0) { t.overdue += b; t.overdueCount += 1; }
  });
  t.pctPastDue = t.net > 0 ? (t.overdue / t.net) * 100 : 0;
  return t;
}

/** 3 tiers, matching the accounting workbook's legend (<30 / 30–60 / >60). toneAt: the days the AR page
    colours each tier by (agingTone). Not-yet-due rows (negative days) count as < 30. */
export function arAgingBuckets(rows) {
  const defs = [
    { key: 'under30', label: '< 30 days past due', max: 29, toneAt: 0 },
    { key: 'd30_60', label: '30–60 days', max: 60, toneAt: 45 },
    { key: 'over60', label: '> 60 days', max: Infinity, toneAt: 90 },
  ].map((d) => ({ ...d, amount: 0, count: 0 }));
  (rows || []).forEach((r) => {
    const b = num(r.balance);
    const d = r.days_past_due == null ? 0 : r.days_past_due;
    const bucket = defs.find((x) => d <= x.max) || defs[defs.length - 1];
    bucket.amount += b;
    bucket.count += 1;
  });
  return defs;
}

/** Mean whole days from invoice to payment over rows carrying days_to_pay (null when none). */
export function avgDaysToPay(rows) {
  const withDays = (rows || []).filter((r) => r.days_to_pay != null);
  return withDays.length ? Math.round(withDays.reduce((s, r) => s + r.days_to_pay, 0) / withDays.length) : null;
}
